import { useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Switch, Title } from '@tryhackme/thm-ui-components';
import { useGetUserQuery } from 'src/common/slices';
import { useTrackEventMutation } from 'src/common/slices/analytics';
import { useGetSearchProgressQuery } from 'src/features/hacktivities/search/content/content.slice';
import { useFeatureIsOn } from 'src/common/growthbook';
import { LearningPathItem } from '../../learn.types';
import { RoadmapTypes, ROADMAP_SWITCH_OPTIONS, ROOM_CODES } from './roadmap.constants';
import { RoadmapType, RoadmapVariant } from './roadmap.types';
import { DynamicDots } from './components/dynamic-dots';
import { StyledRoadmapContainer, StyledSwitchContainer, StyledTitleContainer } from './roadmap.styles';
import { PremiumRoadmap } from './components/premium-roadmap';
import { FreeRoadmap } from './components/free-roadmap';
export const Roadmap = ({
  variant,
  paths,
  recommendedPath,
  isSmall,
  title,
  subtitle,
  hideTitleDivider,
  hideHeader
}: {
  variant: RoadmapVariant;
  paths?: LearningPathItem[];
  recommendedPath?: string;
  isSmall?: boolean;
  title?: string;
  subtitle?: string;
  hideTitleDivider?: boolean;
  hideHeader?: boolean;
}) => {
  const [searchParams] = useSearchParams();
  const isFreeRoadmapSelected = searchParams.get('selectedRoadmap') === RoadmapTypes.FREE_ROADMAP;
  const isRemoveFreeRoadmapEnabled = useFeatureIsOn('remove-free-roadmap');
  const location = useLocation();
  const {
    data: userData
  } = useGetUserQuery();
  const isOnWelcomePage = location.pathname.includes('/welcome');
  const [trackEvent] = useTrackEventMutation();
  const userIsPremium = userData?.data?.user?.isPremium || false;
  const initialRoadmap = useMemo(() => {
    if (isRemoveFreeRoadmapEnabled) {
      return RoadmapTypes.PREMIUM_ROADMAP;
    }
    return isFreeRoadmapSelected ? RoadmapTypes.FREE_ROADMAP : RoadmapTypes.PREMIUM_ROADMAP;
  }, [isRemoveFreeRoadmapEnabled, isFreeRoadmapSelected]);
  const [selectedRoadmap, setSelectedRoadmap] = useState<RoadmapType>(initialRoadmap);
  const roomCodesArray = Object.values(ROOM_CODES).flat();
  const {
    data: searchProgressData
  } = useGetSearchProgressQuery({
    pathIds: paths?.map(path => path.id).join(','),
    roomCodes: roomCodesArray.join(',')
  }, {
    skip: !userData?.data?.user || isOnWelcomePage
  });
  const currentPathCode = userData?.data?.user?.local?.path;
  const isWelcomeVariant = variant === 'welcome';
  const isLearnVariant = variant === 'learn';
  const handleSwitchTab = (roadmapId: RoadmapType) => {
    setSelectedRoadmap(roadmapId);
    if (roadmapId === RoadmapTypes.FREE_ROADMAP) {
      trackEvent({
        event: 'view-free-roadmap',
        properties: {}
      }).catch(() => {});
    }
  };
  const titleText = useMemo(() => {
    if (title) {
      return title;
    }
    return isWelcomeVariant ? 'Welcome to TryHackMe' : 'Cyber Security Learning  Roadmap';
  }, [isWelcomeVariant, title]);
  const subtitleText = useMemo(() => {
    if (subtitle) {
      return subtitle;
    }
    return isWelcomeVariant ? 'Choose your learning path' : 'From fundamental principles to advanced techniques, this roadmap provides clear steps and essential resources to help you build a robust skill set.';
  }, [isWelcomeVariant, subtitle]);
  return <section data-sentry-component="Roadmap" data-sentry-source-file="roadmap.tsx">
      <DynamicDots data-sentry-element="DynamicDots" data-sentry-source-file="roadmap.tsx">
        <StyledRoadmapContainer data-sentry-element="StyledRoadmapContainer" data-sentry-source-file="roadmap.tsx">
          {!hideHeader && <StyledTitleContainer $hideTitleDivider={hideTitleDivider}>
              <Title titleText={titleText} subTitleText={subtitleText} isTitleSmall isInBanner />
            </StyledTitleContainer>}
          {!userIsPremium && isLearnVariant && userData?.data?.user && !isRemoveFreeRoadmapEnabled && <StyledSwitchContainer>
              <Switch options={ROADMAP_SWITCH_OPTIONS} currentOption={selectedRoadmap} onClick={roadmapId => handleSwitchTab(roadmapId)} />
            </StyledSwitchContainer>}

          {selectedRoadmap === RoadmapTypes.PREMIUM_ROADMAP ? <PremiumRoadmap pathProgressData={searchProgressData?.data?.pathProgress} currentPathCode={currentPathCode} recommendedPath={recommendedPath} variant={variant} isSmall={isSmall} /> : <FreeRoadmap roomProgressData={searchProgressData?.data?.roomProgress} switchToPremium={() => {
          setSelectedRoadmap(RoadmapTypes.PREMIUM_ROADMAP);
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }} variant={variant} />}
        </StyledRoadmapContainer>
      </DynamicDots>
    </section>;
};